import {createQueue, frameIsBlack, sampleTimes} from "./video_thumb.js"

const posters = new Map()
// Poster extraction downloads the clip offscreen (no HTTP Range on /media);
// cap concurrency so a video tab does not fan out dozens of full downloads.
const extractQueue = createQueue(2)

function wait(el, event, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${event} timeout`)), timeoutMs)
    const ok = () => {
      clearTimeout(timer)
      el.removeEventListener("error", fail)
      resolve()
    }
    const fail = () => {
      clearTimeout(timer)
      el.removeEventListener(event, ok)
      reject(new Error("media error"))
    }
    el.addEventListener(event, ok, {once: true})
    el.addEventListener("error", fail, {once: true})
  })
}

function nextVideoFrame(video) {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, 80)
    const done = () => {
      clearTimeout(timer)
      resolve()
    }
    if (typeof video.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(() => done())
      return
    }
    requestAnimationFrame(() => done())
  })
}

function sampleImageData(video) {
  const w = 32
  const h = 32
  const canvas = document.createElement("canvas")
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext("2d", {willReadFrequently: true})
  ctx.drawImage(video, 0, 0, w, h)
  return ctx.getImageData(0, 0, w, h)
}

function snapshotDataUrl(video) {
  try {
    const canvas = document.createElement("canvas")
    canvas.width = Math.max(2, video.videoWidth || 64)
    canvas.height = Math.max(2, video.videoHeight || 64)
    canvas.getContext("2d").drawImage(video, 0, 0)
    return canvas.toDataURL("image/jpeg", 0.72)
  } catch {
    return null
  }
}

function isLit(video) {
  if ((video.videoWidth || 0) < 2) return false
  try {
    return !frameIsBlack(sampleImageData(video))
  } catch {
    return false
  }
}

async function seekScan(video) {
  for (const t of sampleTimes(video.duration)) {
    if (t > 0) {
      video.currentTime = t
      await wait(video, "seeked", 2000).catch(() => {})
    }
    if (isLit(video)) return snapshotDataUrl(video)
  }
  return video.videoWidth > 0 ? snapshotDataUrl(video) : null
}

function attachOffscreen(video) {
  video.muted = true
  video.defaultMuted = true
  video.playsInline = true
  video.setAttribute("playsinline", "")
  video.setAttribute("muted", "")
  video.preload = "auto"
  video.style.cssText =
    "position:fixed;left:-9999px;top:0;width:80px;height:80px;opacity:0;pointer-events:none;border:0"
  document.body.appendChild(video)
}

function detach(video) {
  video.pause()
  video.removeAttribute("src")
  video.load()
  video.remove()
}

async function extract(url) {
  const video = document.createElement("video")
  attachOffscreen(video)

  try {
    const ready = wait(video, "loadeddata")
    video.src = url
    if (video.readyState < 2) await ready

    if (isLit(video)) return snapshotDataUrl(video)

    try {
      await video.play()
    } catch {
      return await seekScan(video)
    }

    const stopAt = performance.now() + 4000
    while (performance.now() < stopAt && !video.ended) {
      await nextVideoFrame(video)
      if (isLit(video)) return snapshotDataUrl(video)
    }
    video.pause()
    return video.videoWidth > 0 ? snapshotDataUrl(video) : null
  } finally {
    detach(video)
  }
}

function posterFor(url) {
  if (posters.has(url)) return posters.get(url)
  const job = extractQueue(() => extract(url))
    .then((data) => {
      if (!data) posters.delete(url)
      return data
    })
    .catch(() => {
      posters.delete(url)
      return null
    })
  posters.set(url, job)
  return job
}

export const VideoThumb = {
  mounted() {
    this.show()
  },
  updated() {
    this.show()
  },
  async show() {
    const url = this.el.dataset.src
    const img = this.el.querySelector("img")
    if (!url || !img) return
    const data = await posterFor(url)
    if (!this.el.isConnected || !data) return
    img.src = data
    img.classList.remove("opacity-0")
  },
}
